import React, { useRef, useState, useEffect } from "react";
import { Stack } from "@fluentui/react";
import { v4 as uuidv4 } from "uuid";

import styles from "./Chat.module.css";
import { useBoolean } from "@fluentui/react-hooks";
import {
  ChatMessage,
  ConversationRequest,
  Citation,
  ChatResponse,
  Conversation,
} from "../../api";
import { useAuthenticatedApi } from "../../api/AuthenticatedApiContext";
import { Answer } from "../../components/Answer";
import Layout from "../layout/Layout";
import { ChatMessageContainer } from "../../components/ChatMessageContainer/ChatMessageContainer";
import { CitationPanel } from "../../components/CitationPanel/CitationPanel";
import { ChatHistoryPanel } from "../../components/ChatHistoryPanel/ChatHistoryPanel";
import { ExampleQuestions } from "../../components/ExampleQuestions/ExampleQuestions";
import { ChatInputArea } from "../../components/ChatInputArea/ChatInputArea";

const OFFSET_INCREMENT = 25;
const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"];

// Helper to add IDs and dates to messages from backend response
const addMessageMetadata = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.map(msg => ({
    ...msg,
    id: msg.id || uuidv4(),
    date: msg.date || new Date().toISOString(),
  }));
};

const Chat = () => {
  const {
    callConversationApi,
    historyList,
    historyUpdate,
    historyDeleteAll,
    historyRead,
    getFrontEndSettings,
  } = useAuthenticatedApi();

  const lastQuestionRef = useRef<string>("");
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const [progressMessage, setProgressMessage] = useState<string>("Generating answer...");
  const [isSendButtonDisabled, setSendButtonDisabled] = useState(false);
  const [activeCitation, setActiveCitation] =
    useState<
      [
        content: string,
        id: string,
        title: string,
        filepath: string,
        url: string,
        metadata: Record<string, any> | null,
      ]
    >();
  const [isCitationPanelOpen, setIsCitationPanelOpen] =
    useState<boolean>(false);
  const [answers, setAnswers] = useState<ChatMessage[]>([]);
  const [toggleSpinner, setToggleSpinner] = React.useState(false);
  const [showContextualMenu, setShowContextualMenu] = React.useState(false);
  const [showContextualPopup, setShowContextualPopup] = React.useState(false);
  const abortFuncs = useRef([] as AbortController[]);
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [userMessage, setUserMessage] = useState("");
  const [expertMode, setExpertMode] = useState(false);
  const [showHistoryBtn, setShowHistoryBtn] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [fetchingChatHistory, setFetchingChatHistory] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [hasMoreRecords, setHasMoreRecords] = useState<boolean>(true);
  const [selectedConvId, setSelectedConvId] = useState<string>("");
  const [hideClearAllDialog, { toggle: toggleClearAllDialog }] =
    useBoolean(true);
  const [clearing, setClearing] = React.useState(false);
  const [clearingError, setClearingError] = React.useState(false);
  const [fetchingConvMessages, setFetchingConvMessages] = React.useState(false);
  const [isSavingToDB, setIsSavingToDB] = React.useState(false);
  const [isInitialAPItriggered, setIsInitialAPItriggered] = useState(false);

  const saveToDB = async (messages: ChatMessage[], convId: string) => {
    if (!convId || !messages.length) {
      return;
    }
    const isNewConversation = !selectedConvId;
    setIsSavingToDB(true);
    await historyUpdate(messages, convId)
      .then(async (res) => {
        if (!res.ok) {
          let errorMessage = "Answers can't be saved at this time.";
          let errorChatMsg: ChatMessage = {
            id: uuidv4(),
            role: ERROR,
            content: errorMessage,
            date: new Date().toISOString(),
          };
          if (!messages) {
            setAnswers([...messages, errorChatMsg]);
            let err: Error = {
              ...new Error(),
              message: "Failure fetching current chat state.",
            };
            throw err;
          }
        }
        let responseJson = await res.json();
        if (isNewConversation && responseJson?.success) {
          const metaData = responseJson?.data;
          const newConversation = {
            id: metaData?.conversation_id,
            title: metaData?.title,
            messages: messages,
            date: metaData?.date,
          };
          setChatHistory((prevHistory) => [newConversation, ...prevHistory]);
          setSelectedConvId(metaData?.conversation_id);
        } else if (responseJson?.success) {
          setMessagesByConvId(convId, messages);
        }
        setIsSavingToDB(false);
        return res as Response;
      })
      .catch((err) => {
        console.error("Error: while saving data", err);
        setIsSavingToDB(false);
      });
  };

  const makeApiRequest = async (question: string, options?: { skip_clarification?: boolean }) => {
    lastQuestionRef.current = question;

    setIsGenerating(true);
    setShowLoadingMessage(true);
    setProgressMessage("Starting...");
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);

    const userMessage: ChatMessage = {
      role: "user",
      content: question,
      id: uuidv4(),
      date: new Date().toISOString(),
    };

    const request: ConversationRequest = {
      id: selectedConvId || conversationId,
      messages: [...answers, userMessage].filter(
        (messageObj) => messageObj.role !== ERROR
      ),
      expert_mode: expertMode,
      skip_clarification: options?.skip_clarification,
    };
    let result = {} as ChatResponse;
    try {
      const response = await callConversationApi(
        request,
        abortController.signal
      );
      if (response?.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        // Helper to process complete NDJSON lines
        const processLine = (line: string) => {
          if (!line.trim()) return;
          try {
            const parsed = JSON.parse(line);

            // Handle progress updates
            if (parsed.progress) {
              setProgressMessage(parsed.progress);
              return;
            }

            // Handle final response
            result = parsed;
            setShowLoadingMessage(false);
            if (result.error) {
              setAnswers([
                ...answers,
                userMessage,
                {
                  role: "error",
                  content: result.error,
                  id: "",
                  date: "",
                },
              ]);
            } else if (result.choices?.[0]?.messages) {
              const newMessages = addMessageMetadata(result.choices[0].messages);
              setAnswers([
                ...answers,
                userMessage,
                ...newMessages,
              ]);
            }
          } catch (e) {
            console.error("Failed to parse NDJSON line:", line.substring(0, 200), "error:", e);
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Append new chunk to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process all complete lines (those ending with newline)
          let newlineIndex;
          while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
            const line = buffer.substring(0, newlineIndex);
            buffer = buffer.substring(newlineIndex + 1);
            processLine(line);
          }
        }

        // Process any remaining data after stream ends
        if (buffer.trim()) {
          processLine(buffer);
        }

        // Only save to DB if we got a valid response (not an error)
        if (result.choices?.[0]?.messages) {
          const updatedMessages = [
            ...answers,
            userMessage,
            ...addMessageMetadata(result.choices[0].messages),
          ];
          setAnswers(updatedMessages);
          saveToDB(updatedMessages, selectedConvId || conversationId);
        }
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        if (e instanceof Error) {
          alert(e.message);
        } else {
          alert(
            "An error occurred. Please try again. If the problem persists, please contact the site administrator."
          );
        }
      }
      setAnswers([...answers, userMessage]);
    } finally {
      setIsGenerating(false);
      setShowLoadingMessage(false);
      setProgressMessage("Generating answer...");  // Reset for next request
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
    }

    return abortController.abort();
  };

  const clearChat = () => {
    lastQuestionRef.current = "";
    setActiveCitation(undefined);
    setAnswers([]);
    setConversationId(uuidv4());
    setSelectedConvId("");
  };

  const stopGenerating = () => {
    abortFuncs.current.forEach((a) => a.abort());
    setShowLoadingMessage(false);
    setIsGenerating(false);
  };

  useEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [showLoadingMessage]);

  const onShowCitation = (citation: Citation) => {
    // Parse metadata if it's a string, otherwise use as-is
    const metadata = typeof citation.metadata === 'string'
      ? JSON.parse(citation.metadata)
      : citation.metadata;

    setActiveCitation([
      citation.content,
      citation.id,
      citation.title ?? "",
      citation.filepath ?? "",
      citation.url ?? "",
      metadata,
    ]);
    setIsCitationPanelOpen(true);
    setShowHistoryPanel(false);
  };

  const onClearAllChatHistory = async () => {
    toggleToggleSpinner(true);
    setClearing(true);
    const response = await historyDeleteAll();
    if (!response.ok) {
      setClearingError(true);
    } else {
      setChatHistory([]);
      toggleClearAllDialog();
      setShowContextualPopup(false);
      setAnswers([]);
      setSelectedConvId("");
    }
    setClearing(false);
    toggleToggleSpinner(false);
  };

  const onHideClearAllDialog = () => {
    toggleClearAllDialog();
    setTimeout(() => {
      setClearingError(false);
    }, 2000);
  };

  const onShowContextualMenu = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.preventDefault(); // don't navigate
      setShowContextualMenu(true);
      setShowContextualPopup(true);
    },
    []
  );

  const onHideContextualMenu = React.useCallback(
    () => setShowContextualMenu(false),
    []
  );

  const onSetShowHistoryPanel = () => {
    if (!showHistoryPanel) {
      setIsCitationPanelOpen(false);
    }
    setShowHistoryPanel((prevState) => !prevState);
  };

  const getMessagesByConvId = (id: string) => {
    const conv = chatHistory.find((obj) => String(obj.id) === String(id));
    if (conv) {
      return conv?.messages || [];
    }
    return [];
  };

  const setMessagesByConvId = (id: string, messagesList: ChatMessage[]) => {
    const tempHistory = [...chatHistory];
    const matchedIndex = tempHistory.findIndex(
      (obj) => String(obj.id) === String(id)
    );
    if (matchedIndex > -1) {
      tempHistory[matchedIndex].messages = messagesList;
    }
  };

  const onSelectConversation = async (id: string) => {
    if (isGenerating) {
      // If response is being generated, prevent switching threads
      return;
    }
    if (!id) {
      console.error("No conversation Id found");
      return;
    }
    const messages = getMessagesByConvId(id);
    if (messages.length === 0) {
      setFetchingConvMessages(true);
      const responseMessages = await historyRead(id);
      setAnswers(responseMessages);
      setMessagesByConvId(id, responseMessages);
      setFetchingConvMessages(false);
    } else {
      setAnswers(messages);
    }
    setSelectedConvId(id);
  };

  useEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "instant" });
  }, [selectedConvId]);

  const onHistoryTitleChange = (id: string, newTitle: string) => {
    const tempChatHistory = [...chatHistory];
    const index = tempChatHistory.findIndex((obj) => obj.id === id);
    if (index > -1) {
      tempChatHistory[index].title = newTitle;
      setChatHistory(tempChatHistory);
    }
  };

  const toggleToggleSpinner = (toggler: boolean) => {
    setToggleSpinner(toggler);
  };

  useEffect(() => {
    setIsInitialAPItriggered(true);
  }, []);

  useEffect(() => {
    if (isInitialAPItriggered) {
      (async () => {
        const response = await getFrontEndSettings();
        if (response.CHAT_HISTORY_ENABLED) {
          handleFetchHistory();
          setShowHistoryBtn(true);
        }
      })();
    }
  }, [isInitialAPItriggered]);

  const onHistoryDelete = (id: string) => {
    const tempChatHistory = [...chatHistory];
    tempChatHistory.splice(
      tempChatHistory.findIndex((a) => a.id === id),
      1
    );
    setChatHistory(tempChatHistory);
    if (id === selectedConvId) {
      lastQuestionRef.current = "";
      setActiveCitation(undefined);
      setAnswers([]);
      setSelectedConvId("");
    }
  };

  const handleFetchHistory = async () => {
    if (fetchingChatHistory || !hasMoreRecords) {
      return;
    }
    setFetchingChatHistory(true);
    await historyList(offset).then((response) => {
      if (Array.isArray(response)) {
        setChatHistory((prevData) => [...prevData, ...response]);
        if (response.length === OFFSET_INCREMENT) {
          setOffset((offset) => (offset += OFFSET_INCREMENT));
          // Stopping offset increment if there were no records
        } else if (response.length < OFFSET_INCREMENT) {
          setHasMoreRecords(false);
        }
      } else {
        setChatHistory([]);
      }
      setFetchingChatHistory(false);
      return response;
    });
  };

  const loadingMessageBlock = () => {
    return (
      <React.Fragment key="generating-answer">
        <div className={styles.chatMessageUser}>
          <div className={styles.chatMessageUserMessage}>
            {lastQuestionRef.current}
          </div>
        </div>
        <div className={styles.chatMessageGpt} data-testid="generatingAnswer">
          <Answer
            answer={{
              answer: progressMessage,
              citations: [],
            }}
            onCitationClicked={() => null}
            index={0}
          />
        </div>
      </React.Fragment>
    );
  };
  const showWelcomeScreen =
    !fetchingConvMessages && !lastQuestionRef.current && answers.length === 0;
  const showCitationPanel =
    answers.length > 0 && isCitationPanelOpen && activeCitation;

  const handleExampleQuestionSelect = (question: string) => {
    makeApiRequest(question);
  };

  const handleClarificationSubmit = (originalQuery: string, selectedAnswers: Record<string, string>) => {
    const contextParts = Object.values(selectedAnswers);
    const contextString = contextParts.join(", ");
    const expandedQuestion = `${originalQuery} (Context: ${contextString})`;
    makeApiRequest(expandedQuestion, { skip_clarification: true });
  };

  const handleFollowUpClick = (suggestion: string) => {
    makeApiRequest(suggestion);
  };

  return (
    <Layout
      toggleSpinner={toggleSpinner}
      showHistoryBtn={showHistoryBtn}
      onSetShowHistoryPanel={onSetShowHistoryPanel}
      showHistoryPanel={showHistoryPanel}
      onNewChat={clearChat}
    >
      <div className={styles.container}>
        <Stack horizontal className={styles.chatRoot}>
          {showHistoryPanel && (
            <ChatHistoryPanel
              chatHistory={chatHistory}
              clearing={clearing}
              clearingError={clearingError}
              fetchingChatHistory={fetchingChatHistory}
              fetchingConvMessages={fetchingConvMessages}
              handleFetchHistory={handleFetchHistory}
              hideClearAllDialog={hideClearAllDialog}
              isLoading={isLoading}
              isSavingToDB={isSavingToDB}
              onClearAllChatHistory={onClearAllChatHistory}
              onHideClearAllDialog={onHideClearAllDialog}
              onHideContextualMenu={onHideContextualMenu}
              onHistoryDelete={onHistoryDelete}
              onHistoryTitleChange={onHistoryTitleChange}
              onSelectConversation={onSelectConversation}
              onShowContextualMenu={onShowContextualMenu}
              selectedConvId={selectedConvId}
              setShowHistoryPanel={setShowHistoryPanel}
              showContextualMenu={showContextualMenu}
              showContextualPopup={showContextualPopup}
              showLoadingMessage={showLoadingMessage}
              toggleClearAllDialog={toggleClearAllDialog}
              toggleToggleSpinner={toggleToggleSpinner}
            />
          )}
          <div
            className={`${styles.chatContainer} ${styles.MobileChatContainer} ${showWelcomeScreen ? styles.welcomeMode : ""}`}
          >
            {showWelcomeScreen ? (
              <div className={styles.welcomeContent}>
                <ExampleQuestions
                  onQuestionSelect={handleExampleQuestionSelect}
                  expertMode={expertMode}
                  onExpertModeChange={setExpertMode}
                />
                <ChatInputArea
                  clearOnSend
                  placeholder="Ask about UK drone regulations..."
                  disabled={isGenerating}
                  onSend={(question) => makeApiRequest(question)}
                  onClearChat={clearChat}
                  hasMessages={answers.length > 0}
                  isGenerating={isGenerating}
                  onStopGenerating={stopGenerating}
                  expertMode={expertMode}
                  onExpertModeChange={setExpertMode}
                />
              </div>
            ) : (
              <>
                <div
                  className={styles.chatMessageStream}
                  style={{ marginBottom: isGenerating ? "40px" : "0px" }}
                >
                  <ChatMessageContainer
                    answers={answers}
                    fetchingConvMessages={fetchingConvMessages}
                    onShowCitation={onShowCitation}
                    conversationId={conversationId}
                    onClarificationSubmit={handleClarificationSubmit}
                    onFollowUpClick={handleFollowUpClick}
                    isGenerating={isGenerating}
                  />
                  {showLoadingMessage && loadingMessageBlock()}
                  <div data-testid="streamendref-id" ref={chatMessageStreamEnd} />
                </div>
                <ChatInputArea
                  clearOnSend
                  placeholder="Ask a follow-up question..."
                  disabled={isGenerating}
                  onSend={(question) => makeApiRequest(question)}
                  onClearChat={clearChat}
                  hasMessages={answers.length > 0}
                  isGenerating={isGenerating}
                  onStopGenerating={stopGenerating}
                  expertMode={expertMode}
                  onExpertModeChange={setExpertMode}
                />
              </>
            )}
          </div>
        </Stack>
        {showCitationPanel && (
          <CitationPanel
            activeCitation={activeCitation}
            setIsCitationPanelOpen={setIsCitationPanelOpen}
          />
        )}
      </div>
    </Layout>
  );
};

export default Chat;
